import { Injectable } from '@angular/core'
import { LogService } from 'htv-lib'
import { MonitoringMemoryData, MonitoringStorageData } from '../interfaces/monitoring'
import { PowerState } from '../interfaces/power-state'
import { NetworkConnectionType, NetworkData, NetworkIpMode, NetworkState } from '../interfaces/network'
import { ChannelPosition } from '../pages/channel/channel-position'
import { DVBTChannel, UDPChannel } from '../interfaces/channel'
import { BehaviorSubject } from 'rxjs'
import { WebosHcapExternalInputList } from './webos-hcap-types'
import { ExternalInputData, ExternalInputType } from '../services/external-input.service'

declare const hcap: any | undefined

@Injectable({ providedIn: 'root' })
export class WebOSHCAP {
  constructor(private logService: LogService) {
    // do nothing
  }

  getBrowserDebugMode(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      hcap.property.getProperty({
        key: 'browser_debug_mode',
        onSuccess: (obj: any) => {
          resolve(obj?.value == '1')
        },
        onFailure: (err: any) => {
          this.logService.error(`[browser_debug_mode] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setBrowserDebugMode(enable: boolean): void {
    hcap.system.setBrowserDebugMode({
      debugMode: enable,
      onSuccess: () => {
        this.logService.info(`Debug mode: ${enable}`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setBrowserDebugMode] ${err?.errorMessage}`)
      },
    })
  }

  setHcapMode(): void {
    hcap.mode.setHcapMode({
      mode: hcap.mode.HCAP_MODE_1,
      onSuccess: () => {
        this.logService.info(`HCAP mode: 1`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setHcapMode] ${err?.errorMessage}`)
      },
    })
  }

  getHotelMode(): Promise<string> {
    return new Promise((resolve, reject) => {
      hcap.property.getHotelMode({
        onSuccess: (obj: any) => {
          resolve(obj.hotelMode)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getHotelMode] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setHotelMode(hotelMode: any): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.property.setHotelMode({
        hotelMode: JSON.stringify(hotelMode),
        onSuccess: () => {
          this.logService.info(`Hotel mode configured`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setHotelMode] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setProperty(key: string, value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.property.setProperty({
        key: key,
        value: value,
        onSuccess: () => {
          this.logService.info(`[${key}] ${value}`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[${key}] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setDisplayResolution(value: string): void {
    this.setProperty('display_resolution', value)
  }

  setTVChannelFloatingUI(value: string): void {
    this.setProperty('tv_channel_attribute_floating_ui', value)
  }

  setBootSequenceOption(value: string): void {
    this.setProperty('boot_sequence_option', value)
  }

  setInstantPowerOnMode(value: string): void {
    this.setProperty('instant_power', value)
  }

  setApplicationChannelControl(value: string): void {
    this.setProperty('application_channel_control', value)
  }

  setTvVolumeUI(value: string): void {
    this.setProperty('tv_volume_ui', value)
  }

  setDefaultSubtitleLanguage(value: string): void {
    this.setProperty('default_subtitle_language', value)
  }

  setDefaultAudioLanguage(value: string): void {
    this.setProperty('default_audio_language', value)
  }

  setSecurityLevel(value: string): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.property.setProperty({
        key: 'security_level',
        value: value,
        onSuccess: () => {
          this.logService.info(`[security_level] ${value}`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.info(`[security_level] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getStringProperty(key: string): Promise<string> {
    return new Promise((resolve, reject) => {
      hcap.property.getProperty({
        key: key,
        onSuccess: (obj: any) => {
          resolve(obj.value)
        },
        onFailure: (err: any) => {
          this.logService.error(`[${key}] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getModel(): Promise<string> {
    return this.getStringProperty('model_name')
  }

  getManufacturer(): Promise<string> {
    return this.getStringProperty('platform_manufacturer')
  }

  getSerialNumber(): Promise<string> {
    return this.getStringProperty('serial_number')
  }

  getFirmwareVersion(): Promise<string> {
    return this.getStringProperty('platform_version')
  }

  getXaitVersion(): Promise<string> {
    return this.getStringProperty('xait_version')
  }

  getCpuUsage(): Promise<number> {
    return new Promise((resolve, reject) => {
      hcap.system.getCpuUsage({
        onSuccess: (obj: any) => {
          resolve(obj.percentage)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getCpuUsage] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getMemoryUsage(): Promise<MonitoringMemoryData> {
    return new Promise((resolve, reject) => {
      hcap.system.getMemoryUsage({
        onSuccess: (obj: any) => {
          resolve({
            availableMemory: obj.freememory,
            totalMemory: obj.totalmemory,
          })
        },
        onFailure: (err: any) => {
          this.logService.error(`[getMemoryUsage] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return Promise.resolve([])
  }

  reboot(): void {
    hcap.power.reboot({
      onSuccess: () => {
        this.logService.info(`[reboot] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[reboot] ${err?.errorMessage}`)
      },
    })
  }

  getPowerState(): Promise<PowerState | undefined> {
    return new Promise(resolve => {
      hcap.power.getPowerMode({
        onSuccess: (obj: any) => {
          const powerState = obj.mode == 1 ? PowerState.NORMAL : PowerState.STANDBY
          resolve(powerState)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getPowerState] ${err?.errorMessage}`)
          resolve(undefined)
        },
      })
    })
  }

  setPowerState(state: PowerState): void {
    hcap.power.setPowerMode({
      mode: state == PowerState.STANDBY ? hcap.power.PowerMode.WARM : hcap.power.PowerMode.NORMAL,
      onSuccess: () => {
        this.logService.info(`[setPowerState] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[setPowerState] ${err?.errorMessage}`)
      },
    })
  }

  getNetworkState(): Promise<NetworkState> {
    return new Promise(resolve => {
      hcap.network.getNetworkInformation({
        onSuccess: (obj: any) => {
          const networkState = obj.internet_available ? NetworkState.CONNECTED : NetworkState.DISCONNECTED
          resolve(networkState)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getNetworkState] ${err?.errorMessage}`)
          resolve(NetworkState.UNKNOWN)
        },
      })
    })
  }

  getNetworkData(): Promise<NetworkData> {
    return new Promise((resolve, reject) => {
      hcap.network.getNetworkInformation({
        onSuccess: (obj: any) => {
          const networkData: NetworkData = {
            type: NetworkConnectionType.UNKNOWN,
            macAddress: '',
            ipAddress: obj.ip_address,
            ipMode: NetworkIpMode.UNKNOWN,
            subnetMask: obj.subnet_mask,
            gateway: obj.gateway,
            primaryDns: obj.dns1_address,
            secondaryDns: obj.dns2_address,
            proxy: '',
          }
          resolve(networkData)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getNetworkInformation] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getVolume(): Promise<number> {
    return new Promise((resolve, reject) => {
      hcap.volume.getVolumeLevel({
        onSuccess: (obj: any) => {
          resolve(obj.level)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getVolume] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setVolume(volume: number): Promise<number> {
    return new Promise((resolve, reject) => {
      hcap.volume.setVolumeLevel({
        level: volume,
        onSuccess: () => {
          resolve(volume)
        },
        onFailure: (err: any) => {
          this.logService.error(`[setVolume] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  async getVolumeMute(): Promise<boolean> {
    const volume = await this.getVolume()
    return Promise.resolve(volume == 0)
  }

  volumeBeforeMute: number | undefined = undefined
  async setVolumeMute(muted: boolean): Promise<boolean> {
    if (muted) {
      this.volumeBeforeMute = await this.getVolume()
      await this.setVolume(0)
      return Promise.resolve(true)
    } else {
      const volumeToSet = this.volumeBeforeMute ?? 1
      await this.setVolume(volumeToSet)
      this.volumeBeforeMute = undefined
      return Promise.resolve(false)
    }
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return new Promise((resolve, reject) => {
      hcap.externalinput.getExternalInputList({
        onSuccess: async (obj: WebosHcapExternalInputList) => {
          resolve(
            await Promise.all(
              obj.list.map(async e => ({
                id: `${this.getExternalInputType(e.type)}-${e.index + 1}`,
                type: this.getExternalInputType(e.type),
                index: e.index,
                number: e.index + 1,
                connected: await this.isExternalInputConnected(e.type, e.index),
              }))
            )
          )
        },
        onFailure: (err: any) => {
          this.logService.error(`[getExternalInputs] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    document.addEventListener(
      'hdmi_connection_changed',
      (param: any) => {
        if (param && param.index != undefined && param.connected != undefined) {
          const externalInput = {
            id: `${ExternalInputType.HDMI}-${param.index + 1}`,
            type: ExternalInputType.HDMI,
            index: param.index,
            number: param.index + 1,
            connected: param.connected,
          }
          subject.next([externalInput])
        }
      },
      false
    )
  }

  private getExternalInputType(type: number): ExternalInputType {
    switch (type) {
      case 1:
        return ExternalInputType.TV
      case 2:
        return ExternalInputType.COMPOSITE
      case 3:
        return ExternalInputType.SVIDEO
      case 4:
        return ExternalInputType.COMPONENT
      case 5:
        return ExternalInputType.RGB_DVI
      case 6:
        return ExternalInputType.HDMI
      case 7:
        return ExternalInputType.SCART
      case 9:
        return ExternalInputType.OTHERS
      default:
        return ExternalInputType.OTHERS
    }
  }

  private isExternalInputConnected(type: number, index: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      hcap.externalinput.isExternalInputConnected({
        type: type,
        index: index,
        onSuccess: function (s: { isConnected: boolean }) {
          resolve(s.isConnected)
        },
        onFailure: function (err: any) {
          this.logService.error(`[isExternalInputConnected] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setExternalInput(type: string, index: number): Promise<void> {
    let inputType = hcap.externalinput.ExternalInputType.TV
    switch (type) {
      case 'HDMI':
        inputType = hcap.externalinput.ExternalInputType.HDMI
        break
      case 'TV':
        inputType = hcap.externalinput.ExternalInputType.TV
        break
      default:
        inputType = hcap.externalinput.ExternalInputType.TV
        break
    }
    return new Promise((resolve, reject) => {
      hcap.externalinput.setCurrentExternalInput({
        type: inputType,
        index: index,
        onSuccess: () => {
          this.logService.info(`[setExternalInput] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setExternalInput] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setVideoSize(pos: ChannelPosition): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.video.setVideoSize({
        x: pos.left,
        y: pos.top,
        width: pos.width,
        height: pos.height,
        onSuccess: () => {
          this.logService.info(`[setVideoSize] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[setVideoSize] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  startBroadcastChannel(source: DVBTChannel): void {
    hcap.channel.requestChangeCurrentChannel({
      channelType: hcap.channel.ChannelType.RF,
      frequency: source.freqHz,
      programNumber: source.serviceId,
      rfBroadcastType: hcap.channel.RfBroadcastType.TERRESTRIAL,
      onSuccess: () => {
        this.logService.debug(`[startBroadcastChannel] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[startBroadcastChannel] ${err?.errorMessage}`)
      },
    })
  }

  startUDPChannel(source: UDPChannel): void {
    hcap.channel.requestChangeCurrentChannel({
      channelType: hcap.channel.ChannelType.IP,
      ip: source.ip,
      port: source.port,
      ipBroadcastType: source.rtp ? hcap.channel.IpBroadcastType.RTP : hcap.channel.IpBroadcastType.UDP,

      onSuccess: () => {
        this.logService.debug(`[startUDPChannel] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[startUDPChannel] ${err?.errorMessage}`)
      },
    })
  }

  stopChannel(): void {
    hcap.channel.stopCurrentChannel({
      onSuccess: function () {
        this.logService.debug(`[stopChannel] success`)
      },
      onFailure: function (err: any) {
        this.logService.error(`[stopChannel] ${err?.errorMessage}`)
      },
    })
  }

  getCurrentChannel(): Promise<any> {
    return new Promise((resolve, reject) => {
      hcap.channel.getCurrentChannel({
        onSuccess: function (obj: any) {
          this.logService.debug(`[getCurrentChannel] success`)
          resolve(obj)
        },
        onFailure: function (err: any) {
          this.logService.error(`[getCurrentChannel] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getAudioLanguageList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      hcap.channel.getCurrentChannelAudioLanguageList({
        onSuccess: function (obj: any) {
          this.logService.debug(`[getAudioLanguageList] ${obj.list}`)
          resolve(obj.list.split(','))
        },
        onFailure: function (err: any) {
          this.logService.error(`[getAudioLanguageList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getAudioLanguageCurrentIndex(): Promise<number> {
    return new Promise((resolve, reject) => {
      hcap.channel.getCurrentChannelAudioLanguageIndex({
        onSuccess: function (obj: any) {
          this.logService.debug(`[getAudioLanguageCurrentIndex] ${obj.index}`)
          resolve(obj.index)
        },
        onFailure: function (err: any) {
          this.logService.error(`[getAudioLanguageCurrentIndex] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setAudioLanguageCurrentIndex(index: number): void {
    hcap.channel.setCurrentChannelAudioLanguageIndex({
      index: index,
      onSuccess: function () {
        this.logService.debug(`[setAudioLanguageCurrentIndex] Successfully set audio index to ${index}`)
      },
      onFailure: function (err: any) {
        this.logService.error(`[setAudioLanguageCurrentIndex] ${err?.errorMessage}`)
      },
    })
  }

  getSubtitleList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      hcap.channel.getCurrentChannelSubtitleList({
        onSuccess: function (obj: any) {
          this.logService.debug(`[getSubtitleList] ${obj.list}`)
          resolve(obj.list.split(','))
        },
        onFailure: function (err: any) {
          this.logService.error(`[getSubtitleList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getSubtitleCurrentIndex(): Promise<number> {
    return new Promise((resolve, reject) => {
      hcap.channel.getCurrentChannelSubtitleIndex({
        onSuccess: function (obj: any) {
          this.logService.debug(`[getSubtitleCurrentIndex] ${obj.index}`)
          resolve(obj.index)
        },
        onFailure: function (err: any) {
          this.logService.error(`[getSubtitleCurrentIndex] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setSubtitleCurrentIndex(index: number): void {
    hcap.channel.setCurrentChannelSubtitleIndex({
      index: index,
      onSuccess: function () {
        this.logService.debug(`[setSubtitleCurrentIndex] Successfully set subtitle index to ${index}`)
      },
      onFailure: function (err: any) {
        this.logService.error(`[setSubtitleCurrentIndex] ${err?.errorMessage}`)
      },
    })
  }

  getAppList(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      hcap.application.getApplicationList({
        onSuccess: (obj: any) => {
          this.logService.debug(`[getAppList] success`)
          resolve(obj.list)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getAppList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  startApp(appId: string, params: object): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.application.launchApplication({
        id: appId,
        parameters: params,
        onSuccess: () => {
          this.logService.debug(`[startApp] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[startApp] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  stopApp(appId: string): void {
    hcap.application.destroyApplication({
      id: appId,
      onSuccess: () => {
        this.logService.debug(`[stopApp] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[stopApp] ${err?.errorMessage}`)
      },
    })
  }

  installApps(appIds: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.application.installApplications({
        AppList: appIds,
        onSuccess: () => {
          this.logService.debug(`[installApps] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[installApps] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  uninstallApps(appIds: string[]): void {
    hcap.application.removeApplications({
      AppList: appIds,
      onSuccess: () => {
        this.logService.debug(`[uninstallApps] success`)
      },
      onFailure: (err: any) => {
        this.logService.error(`[uninstallApps] ${err?.errorMessage}`)
      },
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    // do nothing
  }

  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    document.addEventListener(
      'hcap_application_focus_changed',
      (param: any) => {
        subject.next(param.eventType == 'focused')
      },
      false
    )
  }

  registerChannelChangedListener(subject: BehaviorSubject<boolean>): void {
    document.addEventListener(
      'channel_changed',
      (param: any) => {
        subject.next(param.result)
      },
      false
    )
  }

  getLocale(): Promise<string> {
    return new Promise((resolve, reject) => {
      hcap.system.getLocale({
        onSuccess: (obj: any) => {
          resolve(obj.specifier)
        },
        onFailure: (err: any) => {
          this.logService.error(`[getLocale] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  setLocale(specifier: string): Promise<void> {
    return new Promise((resolve, reject) => {
      hcap.system.requestLocaleChange({
        specifier: specifier,
        onSuccess: () => {
          this.logService.debug(`[requestLocaleChange] success`)
          resolve()
        },
        onFailure: (err: any) => {
          this.logService.error(`[requestLocaleChange] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }

  getLocaleList(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      hcap.system.getLocaleList({
        onSuccess: (obj: any) => {
          resolve(obj.list.map((e: { specifier: any }) => e.specifier))
        },
        onFailure: (err: any) => {
          this.logService.error(`[getLocaleList] ${err?.errorMessage}`)
          reject()
        },
      })
    })
  }
}
